/**
 * Usulan auto-draft WO — layar Admin Produksi memeriksa sebelum mewujudkan.
 *
 * Butir H `KEPUTUSAN_FINAL_ETAMSIOS_30072026`, Q35/Q36/Q57/Q58.
 *
 * ---------------------------------------------------------------------------
 * ⛔ EMPAT HAL YANG LAYAR INI WAJIB TUNJUKKAN, DAN SEMUANYA MUDAH DIANGGAP
 *    KEBISINGAN
 * ---------------------------------------------------------------------------
 *
 *  1. **`alasan_potong` tiap usulan.** Admin Produksi yang melihat WO berisi
 *     90 alih-alih 150 akan mengira mesinnya salah lalu menambahkan FA — dan
 *     FA yang ditambahkannya bisa membuat FA mepet di dalamnya jebol. Alasan
 *     yang terlihat mengubah "mesinnya aneh" jadi "oh, ada yang mepet".
 *
 *  2. **FA yang DILEWATI.** Daftar berisi 3 usulan terlihat sama saja apakah
 *     ia berangkat dari 3 FA atau 300. Yang nol masuk usulan mana pun adalah
 *     pekerjaan yang menunggu tanpa ada yang tahu.
 *
 *  3. **Deadline paling mepet per usulan.** Itu angka yang menentukan urutan
 *     kerja, dan ia nol bisa disimpulkan dari jumlah atasannya.
 *
 *  4. **Bahwa ini USULAN, bukan WO.** Nol ada yang terbentuk sampai tombolnya
 *     ditekan. Q35: yang otomatis adalah usulannya; yang memutuskan manusia.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import { getRegions } from "@/lib/papanKerjaApi";
import { labelPaket } from "@/lib/faKonfigurasi";
import Hint from "@/components/etams/Hint";
import { toast } from "sonner";

function Baris({ label, nilai }) {
  return (
    <div>
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className="tabular-nums text-sm font-medium text-foreground">{nilai}</span>
    </div>
  );
}

export default function WOUsulDraft() {
  const { profile } = useAuth();
  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(null);
  const [dibuat, setDibuat] = useState({});

  const regionSendiri = profile?.region_id || null;
  const perluPilih = !regionSendiri;
  const [regions, setRegions] = useState([]);
  const [pilihRegion, setPilihRegion] = useState("");
  const regionId = regionSendiri || pilihRegion || undefined;

  useEffect(() => {
    if (!perluPilih) return;
    getRegions()
      .then(({ data: d }) => {
        const daftar = d || [];
        setRegions(daftar);
        if (daftar.length === 1) setPilihRegion(daftar[0].id);
      })
      .catch((e) => setGalat(apiErrorMessage(e)));
  }, [perluPilih]);

  const muatData = useCallback(async () => {
    if (perluPilih && !pilihRegion) {
      setData(null);
      setGalat("");
      setMuat(false);
      return;
    }
    setMuat(true);
    setGalat("");
    try {
      const { data: d } = await api.get("/wo/usul/draft", {
        params: { region_id: regionId },
      });
      setData(d);
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setData(null);
    } finally {
      setMuat(false);
    }
  }, [regionId, perluPilih, pilihRegion]);

  useEffect(() => {
    muatData();
  }, [muatData]);

  const wujudkan = async (u, i) => {
    setSibuk(i);
    setGalat("");
    try {
      const { data: wo } = await api.post("/wo/usul/wujudkan", {
        store_id: u.store_id,
        fa_ids: u.fa_ids,
        notes: `Auto-draft: ${u.alasan_potong}`,
      });
      setDibuat((d) => ({ ...d, [i]: wo }));
      toast.success(`${wo.wo_number} dibuat sebagai draf`);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setSibuk(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Usulan Draf WO</h1>
          {/* ⛔ Butir 4 — bahwa ini USULAN. Layar yang nol menyebutnya membuat
              orang mengira WO-nya sudah terbentuk dan berhenti menekan apa pun. */}
          <p className="mt-1 text-sm text-muted-foreground">
            Sistem mengelompokkan FA yang siap produksi. Belum ada WO yang
            terbentuk sampai Anda menekan tombolnya.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {perluPilih && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">Wilayah</span>
              <select
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                value={pilihRegion}
                onChange={(e) => setPilihRegion(e.target.value)}
              >
                <option value="">Pilih wilayah…</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            onClick={muatData}
            disabled={muat}
            className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Hitung ulang
          </button>
        </div>
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Menghitung…</p>}

      {!muat && perluPilih && !pilihRegion && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Pilih wilayah lebih dulu — FA dan WO tidak boleh bercampur antar wilayah.
        </div>
      )}

      {!muat && data && (
        <>
          <div className="flex flex-wrap gap-6 rounded-xl border border-border bg-card p-4">
            <Baris label="FA siap produksi" nilai={data.jumlah_calon} />
            <Baris label="Usulan WO" nilai={data.usul.length} />
            <Baris label="Tidak masuk usulan" nilai={data.dilewati.length} />
          </div>

          {data.jumlah_calon === 0 && (
            <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
              Belum ada FA yang siap masuk WO di wilayah ini.{" "}
              <Hint>FA baru muncul di sini sesudah uang mukanya dikonfirmasi.</Hint>
            </div>
          )}

          {data.usul.map((u, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md border border-border px-2 py-0.5 text-xs font-semibold text-foreground">
                      {labelPaket(u.wo_type)}
                    </span>
                    <span className="tabular-nums text-sm text-muted-foreground">
                      {u.total_atasan} atasan · {u.fa_ids.length} FA
                    </span>
                    {u.deadline_paling_mepet && (
                      /* ⛔ Butir 3 — angka yang menentukan urutan kerja, dan
                         ia nol bisa disimpulkan dari jumlah atasannya. */
                      <span className="rounded-md border border-warning-soft-border bg-warning-soft px-2 py-0.5 text-xs font-medium text-warning-soft-foreground">
                        deadline {u.deadline_paling_mepet}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground">
                    {u.fa_numbers.join(", ")}
                  </p>
                  {/* ⛔ Butir 1 — tanpa ini, Admin Produksi mengira mesinnya
                      salah lalu menambahkan FA yang membuat FA mepet jebol. */}
                  <p className="text-xs text-muted-foreground">{u.alasan_potong}</p>
                </div>

                <div className="shrink-0">
                  {dibuat[i] ? (
                    <Link
                      to={`/wo/${dibuat[i].id}`}
                      className="rounded-lg border border-success-soft-border bg-success-soft px-4 py-2 text-sm font-semibold text-success-soft-foreground"
                    >
                      {dibuat[i].wo_number} →
                    </Link>
                  ) : (
                    <button
                      type="button"
                      disabled={sibuk === i}
                      onClick={() => wujudkan(u, i)}
                      className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                      {sibuk === i ? "Membuat…" : "Jadikan draf WO"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* ⛔ Butir 2 — daftar 3 usulan terlihat sama saja apakah ia berangkat
              dari 3 FA atau 300. Yang nol masuk usulan mana pun adalah
              pekerjaan yang menunggu tanpa ada yang tahu. */}
          {data.dilewati.length > 0 && (
            <div className="rounded-xl border border-warning-soft-border bg-warning-soft p-4 text-sm text-warning-soft-foreground">
              <p className="font-semibold">
                {data.dilewati.length} FA tidak masuk usulan mana pun
              </p>
              <p className="mt-1">
                {data.dilewati.map((f) => f.fa_number).join(", ")}
              </p>
            </div>
          )}

          {/* K201 butir 6 — FA yang sudah dibayar uang mukanya tapi customer-nya masih punya
              tunggakan lama: sengaja tidak diusulkan, dan alasannya disebut supaya tidak dikira hilang. */}
          {(data.ditahan_tunggakan || []).length > 0 && (
            <div className="rounded-xl border border-warning-soft-border bg-warning-soft p-4 text-sm text-warning-soft-foreground"
                 data-testid="wo-usul-ditahan-tunggakan">
              <p className="font-semibold">
                {data.ditahan_tunggakan.length} FA belum boleh masuk produksi
              </p>
              <p className="mt-1">
                {data.ditahan_tunggakan.map((f) => f.fa_number).join(", ")} — customer-nya masih punya
                tunggakan lama. FA ini baru bisa masuk produksi sesudah tunggakan itu lunas, atau sesudah
                Owner menyetujui lewat layar Tindak Lanjut Pesanan.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
